import { useEffect, useMemo, useState, type FC } from 'react';

/**
 * Os três pontinhos do Brik com uma frase do que ele está fazendo, trocando a
 * cada 2 s enquanto a resposta não começa a aparecer (07/10/2026, pedido dele:
 * "para não parecer que travou"). Usado na Home, na tela cheia e no painel.
 *
 * A frase é verdadeira: antes da consulta ele está pensando; "Consultando sua
 * base de dados" só aparece depois que a API avisa que uma ferramenta vai rodar
 * (o evento `consultando` do PerguntarStream), e com o nome dela a frase diz o
 * que é ("Olhando suas tarefas…"). A última frase de cada lista fica parada: em
 * espera longa, ela não volta para o "Pensando…".
 */

/** Uma rodada de consulta: as ferramentas, e a vez (cada rodada recomeça as frases). */
export interface ConsultaDoBrik {
    ferramentas: string[];
    vez: number;
}

const ANTES_DA_CONSULTA = ['Pensando…', 'Entendendo o que você pediu…', 'Organizando as ideias…'];
const DEPOIS_DA_CONSULTA = ['Consultando sua base de dados…', 'Juntando o que encontrei…', 'Montando a resposta…'];
const DEPOIS_DE_PREPARAR = ['Conferindo os detalhes…', 'Montando a resposta…'];
const PASSO = 2000;

/* O nome de cada ferramenta do Brik AI (AssistenteFerramentas e
   AssistenteFerramentas.Acoes, na API). Ferramenta nova sem frase aqui cai em
   "Consultando sua base de dados…". */
const FRASE_DA_FERRAMENTA: Record<string, { frase: string; depois?: string[] }> = {
    consultar_tarefas: { frase: 'Olhando suas tarefas…' },
    consultar_agenda: { frase: 'Abrindo sua agenda…' },
    consultar_metas: { frase: 'Conferindo as metas…' },
    consultar_oportunidades: { frase: 'Olhando suas oportunidades…' },
    consultar_oportunidade: { frase: 'Abrindo a oportunidade…' },
    buscar_contato: { frase: 'Procurando o contato…' },
    consultar_faturamento_mensal: { frase: 'Somando o faturamento…' },
    consultar_projecao_fechamento: { frase: 'Calculando a projeção do mês…' },
    consultar_desempenho_vendedores: { frase: 'Comparando os vendedores…' },
    consultar_desempenho_equipes: { frase: 'Comparando as equipes…' },
    consultar_resumo_do_painel: { frase: 'Olhando o painel…' },
    consultar_cadastros: { frase: 'Conferindo os cadastros…' },
    consultar_ajuda: { frase: 'Procurando na ajuda…', depois: ['Lendo o artigo…', 'Montando a resposta…'] },
    sugerir_meta: { frase: 'Calculando uma meta…' },
    /* A varredura demora mais que as outras — é a única que lê conversa por
       conversa. As frases do "depois" contam o que está acontecendo de verdade. */
    revisar_vendas_ganhas: {
        frase: 'Abrindo suas oportunidades em aberto…',
        depois: ['Lendo as conversas uma a uma…', 'Procurando pagamento, entrega e confirmação…', 'Separando o que já fechou…'],
    },
    oportunidade: { frase: 'Preparando a mudança na oportunidade…', depois: DEPOIS_DE_PREPARAR },
    tarefa: { frase: 'Preparando a tarefa…', depois: DEPOIS_DE_PREPARAR },
    agenda: { frase: 'Preparando o compromisso…', depois: DEPOIS_DE_PREPARAR },
    contato: { frase: 'Preparando o contato…', depois: DEPOIS_DE_PREPARAR },
};

const frasesDa = (consulta: ConsultaDoBrik | null | undefined): string[] => {
    if (!consulta) return ANTES_DA_CONSULTA;
    const conhecida = consulta.ferramentas.map(f => FRASE_DA_FERRAMENTA[f]).find(Boolean);
    if (!conhecida) return DEPOIS_DA_CONSULTA;
    return [conhecida.frase, ...(conhecida.depois ?? DEPOIS_DA_CONSULTA)];
};

const AssistentePensando: FC<{ consulta?: ConsultaDoBrik | null }> = ({ consulta }) => {
    const frases = useMemo(() => frasesDa(consulta), [consulta]);
    const [indice, setIndice] = useState(0);

    useEffect(() => {
        setIndice(0);
        const t = setInterval(() => setIndice(i => Math.min(i + 1, frases.length - 1)), PASSO);
        return () => clearInterval(t);
    }, [frases]);

    return (
        /* O leitor de tela ouve uma vez que ele está pensando; as frases que
           trocam a cada 2 s ficam fora, para não serem anunciadas uma a uma. */
        <span className="as-digitando com-frase" role="status" aria-label="O Brik AI está pensando">
            <i /><i /><i />
            <span key={`${consulta?.vez ?? 0}-${indice}`} className="as-digitando-frase" aria-hidden="true">
                {frases[indice]}
            </span>
        </span>
    );
};

export default AssistentePensando;
